import { CALCULATION_RESULT_TEMPLATE } from '../../../config/templates';
import { field } from '../../../field-types';
import { defineRepresentation, RepresentationCategory } from '../../../ontology.model';

const RationalExpressionModel = {
  numerator: field.latex({
    label: 'P(x)',
    label_type: 'latex',
    description: 'The numerator polynomial (e.g., $2x - 13$, $5x + 5$)',
    example: '2x - 13',
  }),
  denominator: field.latex({
    label: 'Q(x)',
    label_type: 'latex',
    description: 'The denominator polynomial (e.g., $x^2 - x - 2$)',
    example: 'x^2 - x - 2',
  }),
  expression: field.latex({
    description: 'The full rational expression $\\frac{P(x)}{Q(x)}$',
    example: '\\frac{2x - 13}{x^2 - x - 2}',
    optional: true,
  }),
} as const;

const FactoredDenominatorModel = {
  factors: field.latexArray({
    description: 'List of linear factors in the denominator (e.g., $(x+1)$, $(x-2)$)',
    example: ['(x + 1)', '(x - 2)'],
  }),
  factored: field.latex({
    description: 'The factored form of the denominator',
    example: '(x + 1)(x - 2)',
    optional: true,
  }),
} as const;

const PartialFractionFormModel = {
  template: field.latex({
    description: 'The partial fraction template with unknown constants',
    example: '\\frac{A}{x + 1} + \\frac{B}{x - 2}',
  }),
  constants: field.array(field.string(), {
    description: 'List of unknown constants to solve for (e.g., ["A", "B", "C"])',
    example: ['A', 'B'],
  }),
} as const;

const ConstantValueModel = {
  name: field.string({
    description: 'The name of the constant (e.g., "A", "B", "C")',
    example: 'A',
  }),
  value: field.number({
    description: 'The computed value of the constant',
    example: 5,
  }),
  working: field.latex({
    description: 'The algebraic working showing how this value was found',
    example: '2(-1) - 13 = A((-1) - 2) \\Rightarrow -15 = -3A \\Rightarrow A = 5',
    optional: true,
  }),
} as const;

const PartialFractionDecompositionModel = {
  result: field.latex({
    label: 'Decomposition',
    description: 'The final partial fraction decomposition',
    example: '\\frac{5}{x + 1} - \\frac{3}{x - 2}',
  }),
  constantNames: field.array(field.string(), {
    description: 'List of constant names (e.g., ["A", "B", "C"])',
    example: ['A', 'B'],
    optional: true,
  }),
  constantValues: field.array(field.number(), {
    description: 'List of constant values corresponding to the names',
    example: [5, -3],
    optional: true,
  }),
} as const;

const LinearSystemModel = {
  equations: field.latexArray({
    description: 'System of equations to solve for constants',
    example: ['A + B = 3', '-6A - 2B + C = -13', '9A - 3B + C = 0'],
  }),
  method: field.enum(['substitution', 'comparison', 'matrix'] as const, {
    description: 'The method used to solve the system',
    example: 'substitution',
  }),
} as const;

const ImproperFractionModel = {
  quotient: field.latex({
    label: 'Q(x)',
    description: 'The polynomial quotient from division',
    example: '4',
  }),
  remainder: field.latex({
    label: 'R(x)',
    description: 'The remainder rational expression',
    example: '\\frac{2}{3x - 1}',
  }),
  decomposition: field.latex({
    description: 'The complete decomposition $Q(x) + \\frac{R(x)}{D(x)}$',
    example: '4 + \\frac{2}{3x - 1}',
  }),
} as const;

export const rep = {
  rational_expr: defineRepresentation({
    id: 'rep.pf.rational_expr',
    name: 'Rational Expression',
    tag_ids: ['tag.pf.decomp'],
    description: 'A rational expression $\\frac{P(x)}{Q(x)}$ where $P(x)$ and $Q(x)$ are polynomials.',
    category: RepresentationCategory.Expression,
    type: 'symbolic',
    model: RationalExpressionModel,
    template: '',
    example_data: {
      numerator: '2x - 13',
      denominator: 'x^2 - x - 2',
      expression: '\\frac{2x - 13}{x^2 - x - 2}',
    },
  }),

  factored_denom: defineRepresentation({
    id: 'rep.pf.factored_denom',
    name: 'Factored Denominator',
    tag_ids: ['tag.pf.decomp'],
    description: 'The denominator polynomial expressed as a product of linear factors.',
    category: RepresentationCategory.Form,
    type: 'symbolic',
    model: FactoredDenominatorModel,
    template: '',
    example_data: {
      factors: ['(x + 1)', '(x - 2)'],
      factored: '(x + 1)(x - 2)',
    },
  }),

  pf_template: defineRepresentation({
    id: 'rep.pf.template',
    name: 'Partial Fraction Template',
    tag_ids: ['tag.pf.decomp'],
    description: 'The partial fraction form with unknown constants (e.g., $\\frac{A}{x+1} + \\frac{B}{x-2}$).',
    category: RepresentationCategory.Expression,
    type: 'symbolic',
    model: PartialFractionFormModel,
    template: '',
    example_data: {
      template: '\\frac{A}{x + 1} + \\frac{B}{x - 2}',
      constants: ['A', 'B'],
    },
  }),

  constant_value: defineRepresentation({
    id: 'rep.pf.constant',
    name: 'Constant Value',
    tag_ids: ['tag.pf.decomp'],
    description: 'A solved constant value (e.g., $A = 5$).',
    category: RepresentationCategory.Value,
    type: 'numerical',
    model: ConstantValueModel,
    template: CALCULATION_RESULT_TEMPLATE,
    example_data: {
      name: 'A',
      value: 5,
      working: '2(-1) - 13 = A((-1) - 2) \\Rightarrow -15 = -3A \\Rightarrow A = 5',
    },
  }),

  decomposition: defineRepresentation({
    id: 'rep.pf.decomposition',
    name: 'Partial Fraction Decomposition',
    tag_ids: ['tag.pf.decomp'],
    description: 'The final decomposition of a rational expression into partial fractions.',
    category: RepresentationCategory.Form,
    type: 'symbolic',
    model: PartialFractionDecompositionModel,
    template: '',
    example_data: {
      result: '\\frac{5}{x + 1} - \\frac{3}{x - 2}',
      constantNames: ['A', 'B'],
      constantValues: [5, -3],
    },
  }),

  linear_system: defineRepresentation({
    id: 'rep.pf.linear_system',
    name: 'System of Equations',
    tag_ids: ['tag.pf.decomp'],
    description: 'A system of linear equations to solve for unknown constants.',
    category: RepresentationCategory.Object,
    type: 'symbolic',
    model: LinearSystemModel,
    template: '',
    example_data: {
      equations: ['A + B = 3', '-2A + 3B = -13'],
      method: 'substitution',
    },
  }),

  improper_decomp: defineRepresentation({
    id: 'rep.pf.improper_decomp',
    name: 'Improper Fraction Decomposition',
    tag_ids: ['tag.pf.improper'],
    description: 'The decomposition of an improper rational expression into a polynomial plus a proper fraction.',
    category: RepresentationCategory.Form,
    type: 'symbolic',
    model: ImproperFractionModel,
    template: '',
    example_data: {
      quotient: '4',
      remainder: '\\frac{2}{3x - 1}',
      decomposition: '4 + \\frac{2}{3x - 1}',
    },
  }),
};

export const partialFractionsRepresentations = Object.values(rep);

export const models = {
  'rep.pf.rational_expr': RationalExpressionModel,
  'rep.pf.factored_denom': FactoredDenominatorModel,
  'rep.pf.template': PartialFractionFormModel,
  'rep.pf.constant': ConstantValueModel,
  'rep.pf.decomposition': PartialFractionDecompositionModel,
  'rep.pf.linear_system': LinearSystemModel,
  'rep.pf.improper_decomp': ImproperFractionModel,
} as const;
